import {
  CheckoutType,
  type MicrositeConfig,
  type MicrositeConfigType,
  type MultiProviderConfig,
  ShopType,
  type SingleProviderConfig,
} from 'types';
import type { ProviderConfig } from 'types/ProviderConfig';
import type {
  MicrositeCustomTheme,
  MicrositeTheme,
} from 'types/MicrositeTheme';
import type {
  RemoteMicrositeConfig,
  RemoteThemeConfig,
  RemoteTviConfig,
} from 'types/RemoteMicrositeConfig';
import { ConfigValidationError } from './ConfigValidationError';
import { ProviderConfigList } from 'config/providers';
import { spectrumRedirectUrl } from 'config/redirectUrls';
import {
  asyncEligible,
  getDefaultCrossSellAction,
  starlinkAsyncEligible,
  trialPartnerAsyncEligible,
} from 'config/sharedFunctions';
import { baseTheme } from 'config/themes/baseTheme';

// Per-promoId overrides for fields that can't yet be expressed in the remote
// schema (functions, complex objects, pending backend schema additions).
// Add an entry when migrating a partner. Delete it once the schema covers it.
type PromoIdOverrides = {
  getCheckoutType?: MicrositeConfig['getCheckoutType'];
  getProviderRedirectUrl?: SingleProviderConfig['getProviderRedirectUrl'];
  redirectWhenUnavailable?: SingleProviderConfig['redirectWhenUnavailable'];
  commercialHomepage?: MicrositeConfig['commercialHomepage'];
  qualOnLoad?: MicrositeConfig['qualOnLoad'];
};

const PROMO_ID_OVERRIDES: Record<string, PromoIdOverrides> = {
  '5001127': {
    // AssurantEmbed
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5001198': {
    // SpectrumSpecial
    getProviderRedirectUrl: spectrumRedirectUrl,
    redirectWhenUnavailable: true,
  },
  '5001201': {
    // XfinitySpecial
    commercialHomepage: 'https://comcastbusinessspecial.com',
  },
  '5001305': {
    // EliseAIEmbed
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5001725': {
    // RealPageEmbed
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5001838': {
    // SkylineEmbed
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5002198': {
    // UpdaterInternet
    getCheckoutType: starlinkAsyncEligible,
  },
  '5002435': {
    // Centerfield
    commercialHomepage: 'https://comcastbusinessspecial.com',
  },
  '5002438': {
    // AdAction
    commercialHomepage: 'https://comcastbusinessspecial.com',
  },
  '5002473': {
    // RatePartner
    qualOnLoad: { hostname: 'rate.updater.com', promoId: '5002473' },
  },
  '5002605': {
    // HintIFrame
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5002646': {
    // YardiEmbed
    getCheckoutType: trialPartnerAsyncEligible,
  },
  '5002970': {
    // ApartmentList
    qualOnLoad: { hostname: 'apartmentlist.updater.com', promoId: '5002970' },
  },
};

// We need these b/c they are required by the MicrositeTheme interface.
// When configs are fully migrated to remote, we should revisit that type.
const THEME_DEFAULTS = {
  headerBackground: '#ffffff',
  headerText: '#000000',
  headerBorder: 'transparent',
  background: '#ffffff',
  footerBackground: '#ffffff',
  footerText: '#000000',
  footerBorder: 'transparent',
  progressBar: '#3957E1',
  link: '#3957E1',
  cta: '#3957E1',
  hover: '#2F48BB',
  logoWidth: '168px',
  logoHeight: '40px',
};

function buildTheme(
  themeConfig: RemoteThemeConfig,
  name: string
): MicrositeTheme {
  const baseColors: MicrositeCustomTheme['baseColors'] = {
    link: themeConfig.colorLink ?? THEME_DEFAULTS.link,
    cta: themeConfig.colorCTA ?? THEME_DEFAULTS.cta,
    hover: themeConfig.colorHover ?? THEME_DEFAULTS.hover,
  };

  const customTheme: MicrositeCustomTheme = {
    name,
    code: name, // extraneous required field
    colors: {
      headerBackground:
        themeConfig.colorHeaderBackground ?? THEME_DEFAULTS.headerBackground,
      headerText: themeConfig.colorHeaderText ?? THEME_DEFAULTS.headerText,
      headerBorder:
        themeConfig.colorHeaderBorder ?? THEME_DEFAULTS.headerBorder,
      background: themeConfig.colorBackground ?? THEME_DEFAULTS.background,
      footerBackground:
        themeConfig.colorFooterBackground ?? THEME_DEFAULTS.footerBackground,
      footerText: themeConfig.colorFooterText ?? THEME_DEFAULTS.footerText,
      footerBorder:
        themeConfig.colorFooterBorder ?? THEME_DEFAULTS.footerBorder,
      progressBar: themeConfig.colorProgressBar ?? THEME_DEFAULTS.progressBar,
      ...(themeConfig.colorHeaderGradient && {
        headerGradient: themeConfig.colorHeaderGradient,
      }),
      ...(themeConfig.colorFooterGradient && {
        footerGradient: themeConfig.colorFooterGradient,
      }),
    },
    logoSize: {
      width: themeConfig.headerLogoWidth ?? THEME_DEFAULTS.logoWidth,
      height: themeConfig.headerLogoHeight ?? THEME_DEFAULTS.logoHeight,
    },
    baseColors,
  };

  return {
    ...baseTheme,
    colors: { ...baseTheme.colors, ...baseColors },
    customTheme,
  };
}

// Looks up a ProviderConfig by providerCode, dcCode, or name (case-insensitive).
function resolveProvider(code: string): ProviderConfig | undefined {
  const lower = code.toLowerCase();
  return ProviderConfigList.find(
    (p) =>
      p.providerCode.toLowerCase() === lower ||
      p.dcCode.toLowerCase() === lower ||
      p.name.toLowerCase() === lower
  );
}

function resolveProviderList(codes: string[]): ProviderConfig[] {
  return codes.flatMap((code) => {
    const p = resolveProvider(code);
    if (!p) console.warn(`[remoteConfig] unknown provider code: "${code}"`);
    return p ? [p] : [];
  });
}

function mapCheckoutType(value: string | null): CheckoutType | undefined {
  if (!value) return undefined;
  return (Object.values(CheckoutType) as string[]).includes(value)
    ? (value as CheckoutType)
    : undefined;
}

type ValidatedRemoteMicrositeConfig = RemoteMicrositeConfig & {
  tviConfig: RemoteTviConfig & {
    directDialTFN: string;
    leadConfirmationTFN: string;
  };
};

const REQUIRED_TFNS = ['directDialTFN', 'leadConfirmationTFN'] as const;

// Collects every problem before throwing so one error describes the whole
// misconfiguration, rather than surfacing them one deploy at a time.
function validateRemoteConfig(
  remote: RemoteMicrositeConfig
): asserts remote is ValidatedRemoteMicrositeConfig {
  const { baseConfig, tviConfig } = remote;
  const problems: string[] = [];

  if (
    baseConfig.shopType === 'SINGLE_PROVIDER' &&
    (tviConfig.providers === 'ALL' || tviConfig.providers.length === 0)
  ) {
    problems.push(
      'SINGLE_PROVIDER requires at least one provider in tviConfig.providers'
    );
  }

  REQUIRED_TFNS.forEach((field) => {
    if (!tviConfig[field]?.trim()) {
      problems.push(`tviConfig.${field} is missing`);
    }
  });

  if (problems.length > 0) {
    throw new ConfigValidationError(String(remote.promoId), problems);
  }
}

function buildSharedFields(remote: ValidatedRemoteMicrositeConfig) {
  const { baseConfig, themeConfig, tviConfig } = remote;
  const promoId = String(remote.promoId);
  // themeName is used for logo asset lookup only, Embed doesn't have logos
  // this entire field should be removed once the logos are migrated to remote config
  const themeName = themeConfig.themeName ?? '';
  const overrides = PROMO_ID_OVERRIDES[promoId];

  const appUrl = baseConfig.appUrl ?? tviConfig.appUrl;

  // The config tool emits a null appUrl when a partner's appDomainOverride was
  // never set. It logs server-side and publishes anyway, so warn here where the
  // cause is knowable — checkout would otherwise just fall back to lead mode.
  if (!appUrl) {
    console.warn(
      `[remoteConfig] no appUrl for promoId ${promoId} — async checkout will fall back to lead mode`
    );
  }

  return {
    name: baseConfig.source,
    micrositeCode: promoId,
    displayName: baseConfig.displayName,
    domain: '', // unused but required by type — see TODO in MicrositeConfig.ts
    homepage: baseConfig.homepage ?? '', // empty for EMBED; NotServiceableModal hides its link when empty
    title: baseConfig.pageTitle ?? '', // unused by EMBED (title inside iframe); required by type
    source: baseConfig.source,
    fallbackPhoneNumber: tviConfig.directDialTFN,
    fallbackLeadModePhoneNumber: tviConfig.leadConfirmationTFN,
    defaultCheckoutType: CheckoutType.LEAD_MODE,
    defaultTheme: buildTheme(themeConfig, themeName),
    needsDcSession: false,
    updaterCrossSellActive: Boolean(
      tviConfig.updaterCrossSellProviders?.length
    ),
    noOffersRetry: 2,
    getCheckoutType: overrides?.getCheckoutType ?? asyncEligible,
    appUrl: appUrl ?? undefined,
    ...(overrides?.commercialHomepage != null && {
      commercialHomepage: overrides.commercialHomepage,
    }),
    ...(overrides?.qualOnLoad != null && { qualOnLoad: overrides.qualOnLoad }),
    iFrameConfig: {
      hideHeaderAndFooter: baseConfig.partnerType === 'EMBED',
      ...(baseConfig.whitelistedOrigins.length > 0 && {
        // Production is the only key, because it is the only environment the
        // embedding guard enforces in. whitelistedOrigins arrives as bare host
        // patterns, which is the format the guard expects.
        trustedHosts: {
          production: baseConfig.whitelistedOrigins,
        },
      }),
    },
    ...(baseConfig.gtmCode != null && { gtmCode: baseConfig.gtmCode }),
    ...(baseConfig.enableMicrositeLanding != null && {
      enableMicrositeLanding: baseConfig.enableMicrositeLanding,
    }),
    ...(tviConfig.suppressNormalizedAddressCheck != null && {
      suppressNormalizedAddressCheck: tviConfig.suppressNormalizedAddressCheck,
    }),
    ...(tviConfig.activeServiceCheckoutType != null && {
      activeServiceCheckoutType: mapCheckoutType(
        tviConfig.activeServiceCheckoutType
      ),
    }),
    ...(themeConfig.centerQuizContainer != null && {
      centerQuizContainer: themeConfig.centerQuizContainer,
    }),
  };
}

function buildSingleProviderConfig(
  remote: ValidatedRemoteMicrositeConfig
): SingleProviderConfig {
  const { tviConfig } = remote;
  const overrides = PROMO_ID_OVERRIDES[String(remote.promoId)];

  const [mainProviderCode, ...syntheticCodes] = tviConfig.providers as string[];
  const mainProvider = resolveProvider(mainProviderCode);
  if (!mainProvider) {
    throw new Error(
      `[remoteConfig] unknown main provider: "${mainProviderCode}"`
    );
  }

  const syntheticProviders = resolveProviderList(syntheticCodes);
  const crossSellProviders = tviConfig.crossSellProviders
    ? resolveProviderList(tviConfig.crossSellProviders)
    : undefined;
  const updaterCrossSellProviders = tviConfig.updaterCrossSellProviders
    ? resolveProviderList(tviConfig.updaterCrossSellProviders)
    : undefined;

  return {
    ...buildSharedFields(remote),
    shopType: ShopType.SINGLE_PROVIDER,
    mainProvider,
    getCrossSellAction: getDefaultCrossSellAction,
    ...(syntheticProviders.length > 0 && { syntheticProviders }),
    ...(crossSellProviders && { crossSellProviders }),
    ...(updaterCrossSellProviders && { updaterCrossSellProviders }),
    ...(overrides?.getProviderRedirectUrl != null && {
      getProviderRedirectUrl: overrides.getProviderRedirectUrl,
    }),
    ...(overrides?.redirectWhenUnavailable != null && {
      redirectWhenUnavailable: overrides.redirectWhenUnavailable,
    }),
  };
}

function buildMultiProviderConfig(
  remote: ValidatedRemoteMicrositeConfig
): MultiProviderConfig {
  const { tviConfig } = remote;

  const providers =
    tviConfig.providers === 'ALL' || tviConfig.providers.length === 0
      ? ProviderConfigList
      : resolveProviderList(tviConfig.providers);

  return {
    ...buildSharedFields(remote),
    shopType: ShopType.MULTI_PROVIDER,
    providers,
  };
}

export function buildMicrositeConfig(
  remote: RemoteMicrositeConfig
): MicrositeConfigType {
  validateRemoteConfig(remote);
  if (remote.baseConfig.shopType === 'SINGLE_PROVIDER') {
    return buildSingleProviderConfig(remote);
  }
  return buildMultiProviderConfig(remote);
}
